// src/components/schoolNameStore.js
// ─────────────────────────────────────────────────────────────────────────
//  Single source of truth for the school's display name.
//  Saved from the ⚙ icon on the Login page (School Name Settings popup).
//  Source of truth = the database (/api/school-settings). localStorage is
//  only a fast-read cache so the UI can paint instantly without waiting on
//  the network — it is kept in sync with the server, and is never wiped by
//  a plain localStorage.clear() on logout (see StudentProfile.js).
// ─────────────────────────────────────────────────────────────────────────
import { useState, useEffect } from "react";

const STORAGE_KEY = "schoolName";
export const DEFAULT_SCHOOL_NAME = "Classora";
const API_BASE = "https://schoolcrm-fzcv.onrender.com";

// Plain (non-hook) getter — safe to call from anywhere, including outside components.
// Always returns synchronously from the local cache (never blocks on the network).
export function getSchoolName() {
  if (typeof window === "undefined") return DEFAULT_SCHOOL_NAME;
  try {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    return saved && saved.trim() ? saved.trim() : DEFAULT_SCHOOL_NAME;
  } catch {
    return DEFAULT_SCHOOL_NAME;
  }
}

// Updates the local cache and notifies every mounted component using
// useSchoolName(), without needing a page refresh.
function broadcast(value) {
  try {
    window.localStorage.setItem(STORAGE_KEY, value);
  } catch {
    // localStorage unavailable — value will still broadcast for this session
  }
  window.dispatchEvent(new CustomEvent("schoolNameChanged", { detail: value }));
}

let hasSyncedFromServer = false;

// Pulls the school name that is actually stored in the database and syncs it
// into the local cache. Cheap to call from every component that renders the
// name — only the first call in a page load hits the server. If the server
// is unreachable, whatever is already cached stays as-is (never silently
// falls back to "Classora" just because a fetch failed).
export function syncSchoolNameFromServer() {
  if (typeof window === "undefined" || hasSyncedFromServer) return;
  hasSyncedFromServer = true;
  fetch(`${API_BASE}/api/school-settings`)
    .then(res => res.json())
    .then(json => {
      const serverName = json?.data?.schoolName && json.data.schoolName.trim();
      if (serverName && serverName !== getSchoolName()) {
        broadcast(serverName);
      }
    })
    .catch(() => {
      hasSyncedFromServer = false; // allow a retry on the next mount
    });
}

// Save + broadcast the change immediately (so the UI updates without
// waiting on the network), then persist it to the database so it survives
// logouts, other devices/browsers, and the local cache being cleared.
export function setSchoolName(name) {
  const value = (name || "").trim() || DEFAULT_SCHOOL_NAME;
  broadcast(value);
  fetch(`${API_BASE}/api/school-settings`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ schoolName: value })
  }).catch(() => {
    // Saved locally at least; syncSchoolNameFromServer() will retry next load.
  });
  return value;
}

// Turns the school name into a safe lowercase slug for things like
// support@<slug>.edu.in — e.g. "Green Valley School" -> "greenvalleyschool"
export function schoolNameSlug(name) {
  return (name || DEFAULT_SCHOOL_NAME)
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "") || "school";
}

export function getSchoolEmailDomain(name) {
  return `${schoolNameSlug(name)}.edu.in`;
}

export function hasSchoolNameSet() {
  if (typeof window === "undefined") return false;
  try {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    return !!(saved && saved.trim());
  } catch {
    return false;
  }
}

// React hook — returns the current school name, syncs it from the database
// on mount, and re-renders the component whenever it changes (via the
// settings popup, a server sync, or another tab).
export function useSchoolName() {
  const [schoolName, setSchoolNameState] = useState(getSchoolName);

  useEffect(() => {
    syncSchoolNameFromServer();
    const handleChange = (e) => setSchoolNameState(e?.detail || getSchoolName());
    const handleStorage = (e) => {
      if (!e.key || e.key === STORAGE_KEY) setSchoolNameState(getSchoolName());
    };
    window.addEventListener("schoolNameChanged", handleChange);
    window.addEventListener("storage", handleStorage);
    return () => {
      window.removeEventListener("schoolNameChanged", handleChange);
      window.removeEventListener("storage", handleStorage);
    };
  }, []);

  return schoolName;
}
